import { cn } from '@/utils/classnames';

interface CircularProgressProps {
  /** Progress value 0–100 */
  value: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
}

/**
 * Small SVG ring that fills clockwise from the top.
 * Amber when incomplete, green when value reaches 100.
 */
function CircularProgress({
  value,
  size = 20,
  strokeWidth = 2.5,
  className,
}: CircularProgressProps) {
  const clamped = Math.min(100, Math.max(0, value));
  const isComplete = clamped >= 100;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped / 100);
  const center = size / 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
      className={cn('shrink-0', className)}
    >
      {/* Track */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="var(--color-fx-neutral-200)"
        strokeWidth={strokeWidth}
      />
      {/* Progress arc — starts at 12 o'clock via -90° rotation */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={isComplete ? 'var(--color-fx-green)' : 'var(--color-fx-amber)'}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${center} ${center})`}
      />
    </svg>
  );
}

export { CircularProgress };
